import './career.erb';
import './cargo_alvo.erb';
import './conclusion.erb';
import './course_selection.erb';
import './current_level.erb';
import './dedication_time.erb';
import './education.erb';
import './enem_details.erb';
import './enem_redirect.erb';
import './images/proenem-logo.svg';
import './index.erb';
import './index.rb';
import './location.erb';
import './military_exam.erb';
import './next_action.erb';
import './oab_phase.erb';
import './oab_redirect.erb';
import './objective.erb';
import './phone.erb';
import './practice_mode.erb';
import './preparatorio.erb';
import './salario.erb';
import {
  buildProenemSourcePath,
  clearProenemSetupRedirectContext,
  getProenemSetupRedirectContext
} from 'utils/proenem_setup_redirect_context';
import {
  clearOabSetupRedirectContext,
  resolveOabSetupRedirectParams
} from 'utils/oab_setup_redirect_context';
import { fetchJanusOabRedirectUrl } from 'utils/janus_oab_redirect';
import { fetchProenemRedirectUrl } from 'utils/proenem_redirect';

// Concursos não termina em `conclusion`: o destino sai de `next-action` ou de
// `practice-mode`. Os passos de perfil que saíram do fluxo (career, cargo-alvo,
// salario...) seguem montados no index.erb — produto alterna variações do
// fluxo e devolver um passo é só voltar a listá-lo aqui.
const SEGMENT_STEPS = {
  concursos: ['next-action', 'practice-mode'],
  oab: ['oab-phase', 'current-level', 'oab-redirect', 'phone', 'conclusion'],
  enem: ['enem-details', 'enem-redirect', 'phone', 'conclusion'],
  militares: ['military-exam', 'current-level', 'phone', 'conclusion']
};

// Predicados de visibilidade por step. Um step ausente daqui é sempre visível.
// Só esconda steps na cauda do array: `currentStepIndex` é posicional, então
// esconder um step no meio dessincroniza a navegação.
const STEP_VISIBILITY = {
  'practice-mode': answers => answers.nextAction === 'resolver_questoes',
  conclusion: answers =>
    !(
      (answers.segment === 'oab' && answers.oabFirstAction === 'oab') ||
      (answers.segment === 'enem' && answers.enemFirstAction === 'proenem')
    )
};

const SEGMENT_PRODUCT_MAP = { concursos: 1, militares: 5, oab: 7, enem: 9 };

const SEGMENT_PRODUCT_LABEL = {
  concursos: 'Concursos Públicos e Jurídicos',
  militares: 'Forças Armadas',
  oab: 'OAB',
  enem: 'Enem e Vestibular'
};

// Valores de `first action chosen` no tracking. Os quatro primeiros já alimentam
// funis em produção — não altere as strings.
const FIRST_ACTION = {
  OAB_REDIRECT: 'Continuar estudos',
  OAB_CONTINUE: 'Continue navegando',
  PROENEM_REDIRECT: 'Continuar estudos',
  PROENEM_CONTINUE: 'Continue navegando',
  COURSE: 'guia-de-estudos',
  DASHBOARD: 'dashboard',
  DIRECTED_QUESTIONS: 'questoes-direcionadas',
  FREE_QUESTIONS: 'questoes-livres',
  EXPLORE: 'explorar-site',
  FOCUS_HELP: 'ajuda-para-escolher-foco',
  PLANS: 'conhecer-planos',
  CURATED_NOTEBOOKS: 'cadernos-curados'
};

// Escolhas de concursos que encerram o wizard. A URL de cada uma vem pronta do
// servidor (`data-concursos-destinations`), que já resolveu o rollout
// mesa_to_elite; aqui só se decide o valor de tracking.
const CONCURSOS_DESTINATION_ACTION = {
  explorar: FIRST_ACTION.EXPLORE,
  ajuda_foco: FIRST_ACTION.FOCUS_HELP,
  planos: FIRST_ACTION.PLANS,
  cadernos_curados: FIRST_ACTION.CURATED_NOTEBOOKS,
  filtrar: FIRST_ACTION.FREE_QUESTIONS
};

// Identifica o setup wizard novo (V2) — vai para analytics, para a coluna
// user_preferences.setup_version e como `origin` na URL de destino, para o
// bord suprimir o onboarding guiado de quem acabou de sair do setup.
// setup_v1 = setup antigo; setup_v2 = wizard novo. Não existe setup_v3 de produto.
const SETUP_VERSION = 'setup_v2';

// Resposta explícita "não sei o cargo", gravada em target_job. String vazia é o
// valor que o Skaldic (kratos), o alfheim e o app Flutter já gravam na mesma
// coluna — divergir aqui faria qualquer contagem de "não sei" que filtre por
// `= ''` perder silenciosamente os usuários do Odin. `null` segue significando
// "não respondeu".
const JOB_UNKNOWN = '';

const CATEGORY_KEYWORDS = {
  Tribunais: ['TJ', 'TRF', 'TRT', 'TRE', 'Judiciário', 'Justiça'],
  Policial: [
    'Polícia',
    'Policial',
    'PF',
    'PRF',
    'PC',
    'PM',
    'Delegado',
    'Agente'
  ],
  Fiscal: ['Fiscal', 'Receita', 'SEFAZ', 'Auditor', 'ISS'],
  Controle: ['TCU', 'TCE', 'CGU', 'Controle'],
  Bancária: ['Banco', 'Caixa', 'BB', 'Bancário', 'Escriturário'],
  Jurídica: ['Procurador', 'Defensor', 'Promotor', 'MP'],
  Administrativa: ['Administrativo', 'Ministério', 'Prefeitura', 'INSS'],
  'Saúde e Educação': [
    'Enfermeiro',
    'Médico',
    'Professor',
    'EBTT',
    'Saúde',
    'Educação'
  ]
};

// Cada carreira do passo anterior corresponde a um chip de categoria da tela de
// meta. Usado só para pré-selecionar o chip; o filtro real da lista é o do
// servidor (wizard_careers -> career_ids). 'outro' fica de fora de propósito.
const CAREER_TO_CATEGORY = {
  tribunais: 'Tribunais',
  policial: 'Policial',
  fiscal: 'Fiscal',
  administrativa: 'Administrativa',
  juridica: 'Jurídica',
  saude: 'Saúde e Educação',
  educacao: 'Saúde e Educação',
  bancaria: 'Bancária'
};

const SEARCH_DEBOUNCE_MS = 350;

// O PATCH grava o perfil com `accepts_nested_attributes_for ... update_only:
// true`: toda chave enviada sobrescreve o que está salvo. Pergunta que o fluxo
// não fez fica `null` e apagaria o que a pessoa já respondeu em outro lugar —
// o drawer da Central do Elite grava o perfil sem marcar `chose_product`, então
// o wizard abre depois e zerava escolaridade e estado, que a recomendação
// exige. Só vai o que foi respondido aqui; '' é resposta ("não sei o cargo").
const answeredOnly = attributes =>
  Object.fromEntries(
    Object.entries(attributes).filter(
      ([, value]) => value !== null && value !== undefined
    )
  );

class SetupWizard {
  constructor() {
    this.$root = $('#js-setup-wizard');

    if (!this.$root.length) {
      return;
    }

    this.products = JSON.parse(this.$root.attr('data-products') || '[]');
    this.userName = this.$root.attr('data-user-name') || '';
    this.bordDomain =
      this.$root.attr('data-bord-domain') || 'app.qconcursos.com';
    this.playgroundQuestionsUrl =
      this.$root.attr('data-playground-questions-url') || '';
    this.concursosDestinations = JSON.parse(
      this.$root.attr('data-concursos-destinations') || '{}'
    );
    // Vai no tracking da conclusão: sem ele o Amplitude não separa quem foi
    // mandado para o UniQ de quem foi para a Mesa.
    this.mesaToEliteActive =
      this.$root.attr('data-mesa-to-elite-rollout') === 'true';
    const rawQuestionId = this.$root.attr('data-question-id');
    this.questionId = rawQuestionId || null;

    this.currentStepIndex = 0;
    this.answers = {
      segment: null,
      educationLevel: null,
      cargoAlvo: null,
      careers: [],
      hasOpenNotice: null,
      dedicationTime: null,
      salarioMinCents: null,
      hasOtherPrepCourse: null,
      state: null,
      acceptsOtherStates: null,
      barExamPhase: null,
      barExamSubject: null,
      barExamExperience: null,
      enemRole: null,
      enemSubjects: [],
      targetScore: null,
      desiredCourse: null,
      targetUniversity: null,
      militaryExam: null,
      militaryExperience: null,
      currentLevel: null,
      phone: null,
      selectedCourse: null,
      oabFirstAction: null,
      enemFirstAction: null,
      nextAction: null,
      practiceMode: null
    };

    this.isSaving = false;
    this.searchDebounceTimer = null;
    this.cargoSearchTimer = null;
    this.coursesAbortController = null;
    this.jobsAbortController = null;
    this.activeCategory = 'Todos';

    this.bindEvents();
    this.trackSetupStarted();
    this.showStep('objective');
  }

  trackSetupStarted() {
    window.UserTracker?.emit('event', {
      eventName: 'account setup started',
      data: {
        request_path: window.location.pathname,
        setup_version: SETUP_VERSION
      }
    });
  }

  getSteps() {
    if (!this.answers.segment) {
      return ['objective'];
    }

    const steps = ['objective', ...SEGMENT_STEPS[this.answers.segment]];

    return steps.filter(step =>
      (STEP_VISIBILITY[step] || (() => true))(this.answers)
    );
  }

  getTotalSteps() {
    return this.getSteps().length;
  }

  getCurrentStepId() {
    const steps = this.getSteps();
    return steps[this.currentStepIndex] || 'objective';
  }

  showStep(stepId) {
    this.$root.find('[data-step]').addClass('hidden');
    this.$root.find(`[data-step="${stepId}"]`).removeClass('hidden');

    if (stepId === 'phone') {
      $('#wizard-phone-input').val(this.answers.phone || '');
    }

    if (stepId === 'cargo-alvo') {
      $('#wizard-cargo-alvo-input').val(this.cargoInputValue());
      this.$root
        .find('[data-action="cargo-nao-sei"]')
        .toggleClass('selected', this.answers.cargoAlvo === JOB_UNKNOWN);
      this.hideCargoSuggestions();
    }

    if (stepId === 'salario') {
      const cents = this.answers.salarioMinCents;

      if (cents == null) {
        $('#wizard-salario-slider').val(0);
        $('#wizard-salario-display').text('R$ 0');
      } else {
        $('#wizard-salario-slider').val(cents);
        const reais = Math.round(cents / 100);
        $('#wizard-salario-display').text(
          reais >= 30000 ? 'R$ 30.000+' : `R$ ${reais.toLocaleString('pt-BR')}`
        );
      }
    }

    if (stepId === 'conclusion') {
      this.updateConclusionButtons();
      this.sendInitialConfirmationEmail();
    }

    this.updateProgressBar();
    this.updateNextButton(stepId);
  }

  // A tela de conclusão diz "Enviamos um link de confirmação" — dispara aqui
  // para o texto ser verdade. Flag de instância evita duplicar se o usuário
  // navegar de volta para este step; falha libera retry pelo botão manual.
  async sendInitialConfirmationEmail() {
    if (this.hasSentConfirmationEmail) {
      return;
    }
    this.hasSentConfirmationEmail = true;

    try {
      await Thor.User.send_email_confirmation({ user_id: currentUser.id }, {});
    } catch {
      this.hasSentConfirmationEmail = false;
    }
  }

  // A tela de conclusão é compartilhada pelos 4 segmentos. Concursos v2 não
  // passa mais por course-selection: sem curso, o botão do curso dá lugar ao
  // "Ir para a plataforma" em vez de simplesmente sumir — senão o segmento
  // inteiro fica só com a secundária. Mesmo par que o Skaldic mostra.
  updateConclusionButtons() {
    const isConcursos = this.answers.segment === 'concursos';
    const hasCourse = Boolean(this.answers.selectedCourse);

    $('#conclusion-main-choice').removeClass('hidden');
    $('#conclusion-question-mode-choice').addClass('hidden');

    $('#conclusion-course-btn').toggleClass(
      'hidden',
      !(isConcursos && hasCourse)
    );
    $('#conclusion-questions-btn').toggleClass('hidden', !isConcursos);
    $('#conclusion-default-btn').toggleClass(
      'hidden',
      isConcursos && hasCourse
    );
  }

  updateProgressBar() {
    const $progress = $('#wizard-progress');
    const stepId = this.getCurrentStepId();

    if (stepId === 'conclusion') {
      $progress.addClass('hidden');
      return;
    }

    $progress.removeClass('hidden');
    const total = this.getTotalSteps();
    const current = this.currentStepIndex + 1;

    const $bar = $('#wizard-progress-bar');
    const $text = $('#wizard-progress-text');

    $bar.html(
      Array.from(
        { length: total },
        (_, i) =>
          `<div style="height:6px;flex:1;border-radius:9999px;transition:background 0.3s;background:${i < current ? '#ff5a1f' : '#e5e7eb'}"></div>`
      ).join('')
    );

    $text.text(`${current} de ${total}`);
  }

  updateNextButton(stepId) {
    const $section = $(`[data-step="${stepId}"]`);
    const $btn = $section.find('.wizard-btn-next');

    switch (stepId) {
      case 'objective':
        $btn.prop('disabled', !this.answers.segment);
        break;
      case 'education':
        $btn.prop('disabled', !this.answers.educationLevel);
        break;
      case 'preparatorio':
        $btn.prop('disabled', !this.answers.hasOtherPrepCourse);
        break;
      case 'career':
        $btn.prop('disabled', this.answers.careers.length === 0);
        break;
      case 'cargo-alvo':
        $btn.prop('disabled', this.answers.cargoAlvo === null);
        break;
      case 'dedication-time':
        $btn.prop('disabled', !this.answers.dedicationTime);
        break;
      case 'location':
        $btn.prop('disabled', !this.answers.state);
        break;
      case 'phone':
        $btn.prop(
          'disabled',
          !this.isPhoneValid($('#wizard-phone-input').val())
        );
        break;
      case 'current-level':
        $btn.prop('disabled', !this.answers.currentLevel);
        break;
      case 'oab-phase':
        $btn.prop('disabled', !this.isOabPhaseValid());
        break;
      case 'enem-details':
        $btn.prop('disabled', !this.isEnemDetailsValid());
        break;
      case 'military-exam':
        $btn.prop('disabled', !this.answers.militaryExam);
        break;
      case 'next-action':
        $btn.prop('disabled', !this.answers.nextAction);
        break;
      case 'practice-mode':
        $btn.prop('disabled', !this.answers.practiceMode);
        break;
      default:
        break;
    }
  }

  getPhoneDigits(value) {
    return (value || '').replace(/\D/g, '');
  }

  formatPhone(value) {
    const digits = this.getPhoneDigits(value).slice(0, 11);

    if (digits.length <= 2) {
      return digits.length ? `(${digits}` : '';
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  isPhoneValid(value) {
    const digits = this.getPhoneDigits(value);

    if (!digits) {
      return true;
    }

    return digits.length === 10 || digits.length === 11;
  }

  updatePhoneAnswer(rawValue) {
    const digits = this.getPhoneDigits(rawValue);

    if (!digits) {
      this.answers.phone = null;
    } else if (digits.length === 10 || digits.length === 11) {
      this.answers.phone = this.formatPhone(digits);
    } else {
      this.answers.phone = this.formatPhone(rawValue);
    }

    this.updateNextButton('phone');
  }

  handlePhoneInput(value) {
    const formatted = this.formatPhone(value);
    $('#wizard-phone-input').val(formatted);
    this.updatePhoneAnswer(formatted);
  }

  skipPhone() {
    this.answers.phone = null;
    $('#wizard-phone-input').val('');
    this.advanceFromPhone();
  }

  advanceFromPhone() {
    if (
      this.answers.segment === 'oab' &&
      this.answers.oabFirstAction === 'oab'
    ) {
      this.completeOabRedirect();
      return;
    }

    this.goNext();
  }

  async completeOabRedirect() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    const { questionId, answer, sourceUrl } = resolveOabSetupRedirectParams(
      this.questionId
    );

    try {
      window.UserTracker?.emit('event', {
        eventName: 'account setup completed',
        data: {
          segment: 'OAB',
          setup_version: SETUP_VERSION,
          'first action chosen': FIRST_ACTION.OAB_REDIRECT,
          'question id': questionId,
          'question answer': answer,
          'source url': sourceUrl
        }
      });
    } catch (trackerErr) {
      window.Rollbar?.error(
        'Erro ao emitir evento account setup completed',
        trackerErr
      );
    }

    try {
      window.UserTracker?.emit('event', {
        eventName: 'damasio redirect clicked',
        data: {
          'click source': 'setup wizard',
          'question id': questionId,
          'question answer': answer,
          'source url': sourceUrl
        }
      });
    } catch (trackerErr) {
      window.Rollbar?.error(
        'Erro ao emitir evento damasio redirect clicked',
        trackerErr
      );
    }

    try {
      await this.persistPreferences();
    } catch {
      /* ignore: segue para o SSO mesmo se a persistência falhar */
    }

    const redirectUrl = await fetchJanusOabRedirectUrl(
      questionId,
      answer,
      sourceUrl
    );
    clearOabSetupRedirectContext();
    window.location.href = redirectUrl;
  }

  armProenemRedirectCta() {
    const $cta = this.$root.find('[data-action="proenem-sso-redirect"]');
    const $secondary = this.$root.find('[data-action="proenem-continue"]');

    $cta
      .data('original-html', $cta.html())
      .prop('disabled', true)
      .attr('aria-busy', 'true')
      .addClass('is-loading')
      .text('Redirecionando...');
    $secondary.prop('disabled', true);
  }

  releaseProenemRedirectCta() {
    const $cta = this.$root.find('[data-action="proenem-sso-redirect"]');
    const $secondary = this.$root.find('[data-action="proenem-continue"]');
    const originalHtml = $cta.data('original-html');

    if (originalHtml) {
      $cta.html(originalHtml);
    }

    $cta
      .prop('disabled', false)
      .removeAttr('aria-busy')
      .removeClass('is-loading')
      .removeData('original-html');
    $secondary.prop('disabled', false);
  }

  async completeProenemRedirect() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.armProenemRedirectCta();

    const sourceUrl = window.location.href;

    try {
      window.UserTracker?.emit('event', {
        eventName: 'account setup completed',
        data: {
          segment: 'Enem e Vestibular',
          setup_version: SETUP_VERSION,
          'first action chosen': FIRST_ACTION.PROENEM_REDIRECT,
          'source url': sourceUrl
        }
      });
    } catch (trackerErr) {
      window.Rollbar?.error(
        'Erro ao emitir evento account setup completed',
        trackerErr
      );
    }

    try {
      window.UserTracker?.emit('event', {
        eventName: 'proenem redirect clicked',
        data: {
          'click source': 'setup wizard',
          'source url': sourceUrl
        }
      });
    } catch (trackerErr) {
      window.Rollbar?.error(
        'Erro ao emitir evento proenem redirect clicked',
        trackerErr
      );
    }

    // Handshake em paralelo com a persistência: no 1º clique o save pode
    // atrasar o SSO e estourar timeout do worker frio do ProEnem.
    const proenemContext = getProenemSetupRedirectContext();
    const sourcePath = buildProenemSourcePath({
      questionCode: proenemContext?.questionCode,
      resolverSimulado: proenemContext?.resolverSimulado
    });
    const redirectPromise = fetchProenemRedirectUrl(sourcePath);
    clearProenemSetupRedirectContext();

    try {
      await this.persistPreferences();
    } catch {
      /* ignore: segue para o SSO mesmo se a persistência falhar */
    }

    const redirectUrl = await redirectPromise;

    if (!redirectUrl) {
      this.isSaving = false;
      this.releaseProenemRedirectCta();
      toastr.error(
        'Não foi possível conectar ao ProEnem. Tente novamente em instantes.'
      );
      return;
    }

    window.location.href = redirectUrl;
  }

  isOabPhaseValid() {
    if (
      !this.answers.educationLevel ||
      !this.answers.barExamPhase ||
      !this.answers.barExamExperience
    ) {
      return false;
    }

    if (
      this.answers.barExamPhase === '2a_fase' &&
      !this.answers.barExamSubject
    ) {
      return false;
    }
    return true;
  }

  goNext() {
    const steps = this.getSteps();
    const maxIndex = steps.length - 1;

    if (this.currentStepIndex < maxIndex) {
      this.currentStepIndex++;
      const stepId = this.getCurrentStepId();
      this.showStep(stepId);

      if (stepId === 'course-selection') {
        this.loadCourses();
      }
    }
  }

  chooseDestination($btn) {
    if (this.getCurrentStepId() === 'practice-mode') {
      this.saveAndClose(this.answers.practiceMode, $btn);
      return;
    }

    if (this.answers.nextAction === 'resolver_questoes') {
      this.goNext();
      return;
    }

    this.saveAndClose(this.answers.nextAction, $btn);
  }

  goPrev() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.showStep(this.getCurrentStepId());
    }
  }

  isEnemDetailsValid() {
    if (!this.answers.enemRole) {
      return false;
    }

    if (this.answers.enemRole === 'professor') {
      return this.answers.enemSubjects.length > 0;
    }

    return Boolean(this.answers.desiredCourse);
  }

  handleEnemRoleChange(role) {
    this.answers.enemRole = role;
    const isProfessor = role === 'professor';
    const isStudent = role === 'aluno';

    if (isProfessor) {
      this.answers.desiredCourse = null;
      this.answers.targetScore = null;
      this.answers.targetUniversity = null;
      $(
        '[data-step="enem-details"] [data-select-field="desiredCourse"]'
      ).removeClass('selected');
      $(
        '[data-step="enem-details"] [data-select-field="targetScore"]'
      ).removeClass('selected');
      $(
        '[data-step="enem-details"] [data-select-field="targetUniversity"]'
      ).removeClass('selected');
    }

    if (isStudent) {
      this.answers.enemSubjects = [];
      this.answers.targetScore = null;
      this.answers.targetUniversity = null;
      $(
        '[data-step="enem-details"] [data-multi-field="enemSubjects"]'
      ).removeClass('selected');
      $(
        '[data-step="enem-details"] [data-select-field="targetScore"]'
      ).removeClass('selected');
      $(
        '[data-step="enem-details"] [data-select-field="targetUniversity"]'
      ).removeClass('selected');
    }

    $('#enem-subjects-section').toggleClass('hidden', !isProfessor);
    $('#enem-desired-course-section').toggleClass('hidden', !isStudent);
    this.updateNextButton('enem-details');
  }

  handleOabPhaseChange(phase) {
    this.answers.barExamPhase = phase;
    const show2ndPhase = phase === '2a_fase';

    if (!show2ndPhase) {
      this.answers.barExamSubject = null;
      $(
        '[data-step="oab-phase"] [data-select-field="barExamSubject"]'
      ).removeClass('selected');
    }

    $('#oab-subject-section').toggleClass('hidden', !show2ndPhase);
    this.updateNextButton('oab-phase');
  }

  handleCareerToggle($card, value) {
    if (value === 'outro') {
      this.answers.careers = ['outro'];
      this.$root.find('[data-career-value]').removeClass('selected');
      $card.addClass('selected');
    } else {
      const outroIdx = this.answers.careers.indexOf('outro');

      if (outroIdx >= 0) {
        this.answers.careers.splice(outroIdx, 1);
        this.$root.find('[data-career-value="outro"]').removeClass('selected');
      }

      const idx = this.answers.careers.indexOf(value);

      if (idx >= 0) {
        this.answers.careers.splice(idx, 1);
        $card.removeClass('selected');
      } else {
        this.answers.careers.push(value);
        $card.addClass('selected');
      }
    }

    this.updateNextButton('career');
  }

  bindEvents() {
    this.$root.on('click', '[data-action="next"]', e => {
      e.preventDefault();
      this.goNext();
    });

    this.$root.on('click', '[data-action="prev"]', e => {
      e.preventDefault();
      this.goPrev();
    });

    this.$root.on('click', '[data-action="save-and-close"]', e => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      this.saveAndClose($btn.attr('data-destination'), $btn);
    });

    // Continuar de `next-action` e `practice-mode`: "Resolver questões" ainda
    // pergunta como praticar; qualquer outra escolha já é o destino final.
    this.$root.on('click', '[data-action="choose-destination"]', e => {
      e.preventDefault();
      this.chooseDestination($(e.currentTarget));
    });

    // Conclusão: "Só quero resolver questões" abre a escolha direcionada/livre.
    // Sem curso selecionado não há o que direcionar, então vai direto para livre.
    this.$root.on('click', '[data-action="show-question-mode-choice"]', e => {
      e.preventDefault();

      if (!this.answers.selectedCourse) {
        this.saveAndClose('free', $(e.currentTarget));
        return;
      }

      $('#conclusion-main-choice').addClass('hidden');
      $('#conclusion-question-mode-choice').removeClass('hidden');
    });

    this.$root.on('click', '[data-action="skip-phone"]', e => {
      e.preventDefault();
      this.skipPhone();
    });

    this.$root.on('input', '#wizard-phone-input', e => {
      this.handlePhoneInput(e.target.value);
    });

    // Segment selection (objective step)
    this.$root.on('click', '[data-segment]', e => {
      e.preventDefault();
      const segment = $(e.currentTarget).attr('data-segment');
      this.answers.segment = segment;

      this.$root.find('[data-segment]').removeClass('selected');
      $(e.currentTarget).addClass('selected');
      // O total de passos depende do segmento: sem isto o "1 de 1" do
      // início só se corrige depois do Continuar.
      this.updateProgressBar();
      this.updateNextButton('objective');
    });

    // ENEM role (special handling for conditional sections)
    this.$root.on(
      'click',
      '[data-step="enem-details"] [data-select-field="enemRole"]',
      e => {
        e.preventDefault();
        e.stopPropagation();
        const $card = $(e.currentTarget);
        const value = $card.attr('data-select-value');

        $card
          .closest('.wizard-grid, .wizard-chips')
          .find('[data-select-field="enemRole"]')
          .removeClass('selected');
        $card.addClass('selected');
        this.handleEnemRoleChange(value);
      }
    );

    this.$root.on('input', '#wizard-cargo-alvo-input', e => {
      const value = e.target.value || '';
      this.$root.find('[data-action="cargo-nao-sei"]').removeClass('selected');

      if (value.trim() !== this.cargoInputValue()) {
        this.answers.cargoAlvo = null;
        this.updateNextButton('cargo-alvo');
      }
      this.scheduleCargoSearch(value);
    });

    this.$root.on('focus', '#wizard-cargo-alvo-input', e => {
      this.scheduleCargoSearch(e.target.value || '', 0);
    });

    this.$root.on('blur', '#wizard-cargo-alvo-input', () => {
      window.setTimeout(() => this.hideCargoSuggestions(), 150);
    });

    this.$root.on('keydown', '#wizard-cargo-alvo-input', e =>
      this.handleCargoKeydown(e)
    );

    // `mousedown` com `preventDefault` em vez de `click`: segura o foco no
    // input, então o `blur` acima não agenda o `hideCargoSuggestions` que
    // destruiria a opção antes do clique. O teclado não passa por aqui — as
    // opções não são focáveis, quem seleciona é o `handleCargoKeydown`.
    this.$root.on('mousedown', '[data-cargo-option]', e => {
      e.preventDefault();
      const name = $(e.currentTarget).attr('data-cargo-option');
      this.selectCargoOption(name);
    });

    this.$root.on('click', '[data-action="cargo-nao-sei"]', e => {
      e.preventDefault();
      this.answers.cargoAlvo = JOB_UNKNOWN;
      $('#wizard-cargo-alvo-input').val('');
      this.$root.find('[data-action="cargo-nao-sei"]').addClass('selected');
      this.hideCargoSuggestions();
      this.updateNextButton('cargo-alvo');
    });

    this.$root.on('input', '#wizard-salario-slider', e => {
      const cents = Number(e.target.value);
      this.answers.salarioMinCents = cents;
      const reais = Math.round(cents / 100);
      $('#wizard-salario-display').text(
        reais >= 30000 ? 'R$ 30.000+' : `R$ ${reais.toLocaleString('pt-BR')}`
      );
      this.$root.find('[data-action="salario-skip"]').removeClass('selected');
    });

    this.$root.on('click', '[data-action="salario-skip"]', e => {
      e.preventDefault();
      this.answers.salarioMinCents = null;
      $('#wizard-salario-slider').val(0);
      $('#wizard-salario-display').text('R$ 0');
      $(e.currentTarget).addClass('selected');
    });

    // OAB phase (special handling for conditional subject section)
    this.$root.on(
      'click',
      '[data-step="oab-phase"] [data-select-field="barExamPhase"]',
      e => {
        e.preventDefault();
        e.stopPropagation();
        const $card = $(e.currentTarget);
        const value = $card.attr('data-select-value');

        $card
          .closest('.wizard-grid')
          .find('[data-select-field="barExamPhase"]')
          .removeClass('selected');
        $card.addClass('selected');
        this.handleOabPhaseChange(value);
      }
    );

    // Career multi-select with "outro" exclusivity
    this.$root.on('click', '[data-career-value]', e => {
      e.preventDefault();
      const $card = $(e.currentTarget);
      const value = $card.attr('data-career-value');
      this.handleCareerToggle($card, value);
    });

    // Single-select cards (generic)
    this.$root.on('click', '[data-select-field]', e => {
      e.preventDefault();
      const $card = $(e.currentTarget);
      const field = $card.attr('data-select-field');
      const value = $card.attr('data-select-value');

      // Campos com seções condicionais têm handler dedicado acima.
      if (field === 'barExamPhase' || field === 'enemRole') {
        return;
      }

      this.answers[field] = value;
      $card
        .closest('.wizard-grid, .wizard-chips')
        .find(`[data-select-field="${field}"]`)
        .removeClass('selected');
      $card.addClass('selected');
      this.updateNextButton(this.getCurrentStepId());
    });

    // Multi-select chips (generic)
    this.$root.on('click', '[data-multi-field]', e => {
      e.preventDefault();
      const $chip = $(e.currentTarget);
      const field = $chip.attr('data-multi-field');
      const value = $chip.attr('data-multi-value');

      const arr = this.answers[field];
      const idx = arr.indexOf(value);

      if (idx >= 0) {
        arr.splice(idx, 1);
        $chip.removeClass('selected');
      } else {
        arr.push(value);
        $chip.addClass('selected');
      }

      this.updateNextButton(this.getCurrentStepId());
    });

    // Select inputs (location state)
    this.$root.on('change', '[data-input-field]', e => {
      const field = $(e.target).attr('data-input-field');
      this.answers[field] = e.target.value || null;
      this.updateNextButton(this.getCurrentStepId());
    });

    // Course search
    this.$root.on('input', '#course-search-input', e => {
      this.handleCourseSearch(e.target.value);
    });

    // Category filter
    this.$root.on('click', '[data-category]', e => {
      e.preventDefault();
      const category = $(e.currentTarget).attr('data-category');
      this.activeCategory = category;

      this.$root.find('[data-category]').removeClass('active');
      $(e.currentTarget).addClass('active');

      const query = ($('#course-search-input').val() || '').trim();
      this.fetchCourses(query || undefined, category);
    });

    // Course selection
    this.$root.on('click', '[data-course-id]', e => {
      e.preventDefault();
      const id = parseInt($(e.currentTarget).attr('data-course-id'), 10);
      this.answers.selectedCourse = id;

      this.$root.find('[data-course-id]').removeClass('selected');
      this.$root.find(`[data-course-id="${id}"]`).addClass('selected');
    });

    // OAB: escolher estudar pela OAB (Damásio) -> coleta telefone antes de concluir
    this.$root.on('click', '[data-action="oab-sso-redirect"]', e => {
      e.preventDefault();
      this.answers.oabFirstAction = 'oab';
      this.goNext();
    });

    // OAB: continuar navegando no QC -> telefone -> conclusion.
    // account setup completed dispara só em saveAndClose, para evitar evento
    // duplicado com first action chosen conflitante.
    this.$root.on('click', '[data-action="oab-continue"]', e => {
      e.preventDefault();
      this.answers.oabFirstAction = 'continue';
      clearOabSetupRedirectContext();
      this.goNext();
    });

    // Enem: estudar pelo ProEnem -> SSO imediato (sem step de telefone)
    this.$root.on('click', '[data-action="proenem-sso-redirect"]', e => {
      e.preventDefault();
      this.answers.enemFirstAction = 'proenem';
      this.completeProenemRedirect();
    });

    // Enem: continuar navegando no QC -> telefone -> conclusion.
    this.$root.on('click', '[data-action="proenem-continue"]', e => {
      e.preventDefault();
      this.answers.enemFirstAction = 'continue';
      this.goNext();
    });

    // Avançar a partir do telefone (Continuar)
    this.$root.on('click', '[data-action="phone-next"]', e => {
      e.preventDefault();
      this.advanceFromPhone();
    });

    // Resend email
    this.$root.on('click', '[data-action="resend-email"]', async e => {
      e.preventDefault();
      const $btn = $(e.currentTarget);
      $btn.prop('disabled', true).text('Enviando...');

      try {
        await Thor.User.send_email_confirmation(
          { user_id: currentUser.id },
          {}
        );
        window.UserTracker?.emit('event', {
          eventName: 'email confirmation resent',
          data: {
            'source platform': 'portal',
            'source project': gon?.source_project,
            request_path: window.location.pathname,
            setup_version: SETUP_VERSION
          }
        });
        $btn.text('E-mail enviado!');
      } catch {
        $btn.text('Erro ao enviar. Tente novamente.').prop('disabled', false);
      }
    });

    // Objective next with tracking
    this.$root.on(
      'click',
      '[data-step="objective"] [data-action="next"]',
      () => {
        if (this.answers.segment) {
          window.UserTracker?.emit('event', {
            eventName: 'account setup preferences setted',
            data: {
              segment: SEGMENT_PRODUCT_LABEL[this.answers.segment],
              setup_version: SETUP_VERSION
            }
          });
        }
      }
    );
  }

  getCsrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') || ''
    );
  }

  scheduleCargoSearch(value, delay = SEARCH_DEBOUNCE_MS) {
    if (this.cargoSearchTimer) {
      clearTimeout(this.cargoSearchTimer);
    }

    this.cargoSearchTimer = setTimeout(() => {
      this.fetchCargoSuggestions(value.trim());
    }, delay);
  }

  // O sentinela é valor de contrato, não texto de input: a caixa fica vazia.
  cargoInputValue() {
    const value = this.answers.cargoAlvo;
    return value === null || value === JOB_UNKNOWN ? '' : value;
  }

  selectCargoOption(name) {
    this.answers.cargoAlvo = name;
    $('#wizard-cargo-alvo-input').val(name);
    this.$root.find('[data-action="cargo-nao-sei"]').removeClass('selected');
    this.hideCargoSuggestions();
    this.updateNextButton('cargo-alvo');
  }

  // Fechar a lista encerra a busca. Sem isto, um timer agendado ou uma resposta
  // ainda em voo chama `renderCargoSuggestions` depois e reabre a lista por cima
  // do step já respondido — e como o input perdeu o foco, nenhum blur a fecha.
  hideCargoSuggestions() {
    clearTimeout(this.cargoSearchTimer);
    this.cargoSearchTimer = null;
    this.jobsAbortController?.abort();
    this.jobsAbortController = null;

    const $list = this.$root.find('#wizard-cargo-alvo-list');
    $list.addClass('hidden').empty();
    $('#wizard-cargo-alvo-input')
      .attr('aria-expanded', 'false')
      .removeAttr('aria-activedescendant');
  }

  // Padrão ARIA combobox: o foco nunca sai do input e a opção corrente é
  // apontada por `aria-activedescendant`. As opções não são focáveis, então Tab
  // sai do combobox em vez de parar num nó que o blur vai destruir.
  handleCargoKeydown(e) {
    if (e.key === 'Escape') {
      this.hideCargoSuggestions();
      return;
    }

    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') {
      return;
    }

    const $options = this.cargoOptions();

    if (!$options.length) {
      // Lista fechada: seta para baixo reabre com o termo já digitado.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.scheduleCargoSearch(e.target.value || '', 0);
      }

      return;
    }

    const $active = $options.filter('.active');

    if (e.key === 'Enter') {
      // Sem opção ativa não há o que selecionar: deixa o Enter seguir.
      if (!$active.length) {
        return;
      }

      e.preventDefault();
      this.selectCargoOption($active.attr('data-cargo-option'));
      return;
    }

    e.preventDefault();
    const step = e.key === 'ArrowDown' ? 1 : -1;
    const current = $options.index($active);
    let next;

    if (current === -1) {
      next = step === 1 ? 0 : $options.length - 1;
    } else {
      next = (current + step + $options.length) % $options.length;
    }

    this.setActiveCargoOption(next);
  }

  cargoOptions() {
    return this.$root.find('#wizard-cargo-alvo-list [data-cargo-option]');
  }

  setActiveCargoOption(index) {
    const $options = this.cargoOptions();
    $options.removeClass('active').attr('aria-selected', 'false');

    const $active = $options.eq(index);
    $active.addClass('active').attr('aria-selected', 'true');
    $active[0]?.scrollIntoView?.({ block: 'nearest' });
    $('#wizard-cargo-alvo-input').attr(
      'aria-activedescendant',
      $active.attr('id')
    );
  }

  renderCargoSuggestions(jobs) {
    if (!jobs.length) {
      this.openCargoList(
        '<li class="wizard-combobox-empty" role="presentation">Nenhum cargo encontrado</li>'
      );
      return;
    }

    // `role="option"` no próprio `<li>`, sem botão dentro: `option` não pode
    // conter elemento interativo, e um nó focável aqui viraria armadilha de Tab
    // — o blur do input limpa a lista 150ms depois de o foco chegar nele.
    const items = jobs
      .map((job, index) => {
        const name = this.escapeHtml(job.name);

        return `<li id="wizard-cargo-alvo-option-${index}" role="option" aria-selected="false" class="wizard-combobox-option" data-cargo-option="${name}">${name}</li>`;
      })
      .join('');

    this.openCargoList(items);
  }

  // Erro precisa ser distinto da lista vazia: sem isso, uma indisponibilidade da
  // busca empurra o usuário para o "Não sei o cargo" e o sentinela é gravado
  // como se fosse resposta.
  renderCargoError() {
    this.openCargoList(
      '<li class="wizard-combobox-error" role="alert">Não foi possível buscar agora. Tente de novo.</li>'
    );
  }

  openCargoList(html) {
    this.$root.find('#wizard-cargo-alvo-list').removeClass('hidden').html(html);
    $('#wizard-cargo-alvo-input')
      .attr('aria-expanded', 'true')
      .removeAttr('aria-activedescendant');
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async fetchCargoSuggestions(search) {
    this.jobsAbortController?.abort();
    const abortController = new AbortController();
    this.jobsAbortController = abortController;

    try {
      const params = new URLSearchParams();

      if (search) {
        params.set('q', search);
      }

      const response = await fetch(`/api/initial_setup/jobs?${params}`, {
        credentials: 'include',
        signal: abortController.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        }
      });

      if (!response.ok) {
        this.renderCargoError();
        return;
      }

      const payload = await response.json();
      const jobs = Array.isArray(payload) ? payload : payload.jobs || [];
      this.renderCargoSuggestions(jobs);
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      this.renderCargoError();
    }
  }

  // Course search
  handleCourseSearch(value) {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.fetchCourses(value.trim() || undefined, this.activeCategory);
    }, SEARCH_DEBOUNCE_MS);
  }

  loadCourses() {
    // Pré-seleciona o chip da carreira escolhida (pista visual). A lista em si
    // continua vindo do filtro de carreira do servidor — ver applyCategoryFilter.
    const category = this.careerDerivedCategory() || 'Todos';
    this.activeCategory = category;
    this.$root.find('[data-category]').removeClass('active');
    this.$root.find(`[data-category="${category}"]`).addClass('active');
    $('#course-search-input').val('');
    // Só se chega aqui vindo de `career`; a lista é remontada para a carreira
    // atual, então um curso escolhido antes pode nem constar nela.
    this.answers.selectedCourse = null;
    this.fetchCourses();
  }

  // Categoria derivada da(s) carreira(s) escolhida(s): só retorna algo quando
  // todas mapeiam para a mesma categoria (cobre 1 carreira e o par saúde+educação).
  // Mistura, 'outro' ou vazio -> null (o chip fica em 'Todos').
  careerDerivedCategory() {
    const cats = this.answers.careers.map(c => CAREER_TO_CATEGORY[c]);

    if (cats.length === 0 || cats.some(c => !c)) {
      return null;
    }
    const unique = [...new Set(cats)];
    return unique.length === 1 ? unique[0] : null;
  }

  // Filtro client-side por palavra-chave no nome do curso. Pulado para 'Todos',
  // sem categoria, ou para a categoria da própria carreira — nesse caso o
  // servidor (wizard_careers -> career_ids) já escopou a lista, e as palavras-
  // chave não cobrem todos os nomes reais (ex.: "Administrador" != "Administrativo").
  applyCategoryFilter(guides, category) {
    if (
      !category ||
      category === 'Todos' ||
      category === this.careerDerivedCategory()
    ) {
      return guides;
    }
    const keywords = CATEGORY_KEYWORDS[category] || [];
    return guides.filter(g => {
      const text =
        `${g.title || ''} ${g.institute_name || ''} ${g.opportunity_job_name || ''}`.toLowerCase();
      return keywords.some(kw => text.includes(kw.toLowerCase()));
    });
  }

  async fetchCourses(search, category) {
    const $list = this.$root.find('#wizard-course-list');
    const $spinner = this.$root.find('#course-search-spinner');

    // Clique em categoria dispara fetch sem debounce e pode correr com uma busca
    // textual em andamento; sem abortar, a resposta mais lenta venceria.
    this.coursesAbortController?.abort();
    const abortController = new AbortController();
    this.coursesAbortController = abortController;

    $spinner.show();
    $list.html(
      '<div class="wizard-loading"><div class="spinner" style="width:24px;height:24px;border:2px solid #ff5a1f;border-top-color:transparent;border-radius:50%;animation:spin 0.6s linear infinite"></div></div>'
    );

    try {
      const params = new URLSearchParams();

      if (search) {
        params.set('q', search);
      }

      if (this.answers.state && this.answers.acceptsOtherStates === 'nao') {
        params.set('state', this.answers.state);
      }

      this.answers.careers.forEach(career =>
        params.append('wizard_careers[]', career)
      );
      params.set('per_page', '50');

      const response = await fetch(
        `/api/kratos/bord/study_guides/search?${params}`,
        {
          credentials: 'include',
          signal: abortController.signal,
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          }
        }
      );

      const data = await response.json();
      const guides = this.applyCategoryFilter(
        this.dedupeGuides(data.study_guides || []),
        category
      );

      this.renderCourses(guides);
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      // eslint-disable-next-line no-console
      console.error('[SetupWizard] Erro ao buscar cursos:', err);
      $list.html(
        '<div class="wizard-empty-state">Erro ao buscar cursos. Verifique se o Elasticsearch está ativo.</div>'
      );
    } finally {
      if (this.coursesAbortController === abortController) {
        $spinner.hide();
      }
    }
  }

  // Uma Opportunity pode ter mais de um StudyGuide (não há unicidade no banco), e
  // todos derivam dela o mesmo título/instituto — viram cards idênticos na lista.
  // O id da Opportunity não está no índice, então deduplicamos pelo que o card
  // realmente mostra. O ano entra na chave para não descartar edições distintas
  // do mesmo concurso, que compartilham alias e instituto.
  dedupeGuides(guides) {
    const seen = new Set();

    return guides.filter(guide => {
      const key = [
        guide.opportunity_job_alias,
        guide.opportunity_job_name,
        guide.institute_name,
        guide.contest_year
      ].join('|');

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  renderCourses(guides) {
    const $list = this.$root.find('#wizard-course-list');

    if (!guides.length) {
      $list.html(
        '<div class="wizard-empty-state">Nenhum curso encontrado.</div>'
      );
      return;
    }

    const html = guides
      .map(g => {
        const initials = (g.institute_initials || '').slice(0, 3);
        const jobName =
          g.opportunity_job_alias || g.opportunity_job_name || g.title || '';
        const instituteName = g.institute_name || '';
        const selected = this.answers.selectedCourse === g.id ? 'selected' : '';

        return `
        <button type="button" class="wizard-course-card ${selected}" data-course-id="${g.id}">
          <div class="wizard-course-card__initials">${initials}</div>
          <div class="wizard-course-card__info">
            <span class="wizard-course-card__title">${jobName}</span>
            <span class="wizard-course-card__subtitle">${instituteName}</span>
          </div>
        </button>
      `;
      })
      .join('');

    $list.html(html);
  }

  // Save and redirect
  buildPreferencesData() {
    const segment = this.answers.segment;
    const productId = segment ? SEGMENT_PRODUCT_MAP[segment] : 1;

    return {
      user_preferences: {
        chose_product: true,
        preferred_product_id: productId,
        // Exposto ao bord via GET /api/kratos/bord/user_preferences — sinal
        // durável de que o usuário veio do setup novo.
        setup_version: SETUP_VERSION,
        study_profile_attributes: answeredOnly({
          education_level: this.answers.educationLevel,
          careers:
            this.answers.careers.length > 0
              ? this.answers.careers.join(',')
              : null,
          has_open_notice: this.answers.hasOpenNotice,
          // Sem default para JOB_UNKNOWN: fora do fluxo, o passo de cargo deixa
          // `null` ("não respondeu") e a chave nem vai; o sentinela gravaria um
          // "não sei" que ninguém respondeu. Com o passo no fluxo, o Continuar
          // não libera enquanto o cargo for `null`.
          target_job: this.answers.cargoAlvo,
          dedication_time: this.answers.dedicationTime,
          minimum_salary_cents: this.answers.salarioMinCents,
          state: this.answers.state,
          accepts_other_states: this.answers.acceptsOtherStates,
          bar_exam_phase: this.answers.barExamPhase,
          bar_exam_subject: this.answers.barExamSubject,
          bar_exam_experience: this.answers.barExamExperience,
          enem_subjects:
            this.answers.enemSubjects.length > 0
              ? this.answers.enemSubjects.join(',')
              : null,
          target_score: this.answers.targetScore,
          desired_course: this.answers.desiredCourse,
          target_university: this.answers.targetUniversity,
          military_exam: this.answers.militaryExam,
          military_experience: this.answers.militaryExperience,
          current_level: this.answers.currentLevel,
          phone: this.answers.phone
        })
      },
      wizard_profile: {
        already_pays_other_prep_course: this.answers.hasOtherPrepCourse
          ? this.answers.hasOtherPrepCourse === 'sim'
          : null,
        enem_role: this.answers.enemRole
      }
    };
  }

  async persistPreferences() {
    await Thor.UserPreference.update({}, { data: this.buildPreferencesData() });
  }

  // `origin` marca a navegação como vinda do setup novo — é o sinal imediato
  // (junto do setup_version persistido) para o destino não abrir o onboarding
  // guiado em cima de quem precisa ver o botão de matrícula.
  withOriginParam(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}origin=${SETUP_VERSION}`;
  }

  // `destinationType`: 'course' | 'directed' | 'free', ou uma chave de
  // CONCURSOS_DESTINATION_ACTION. Ausente = fluxo legado (segmentos sem
  // course-selection, que mantêm o botão único).
  resolveDestination(destinationType) {
    const concursosUrl = this.concursosDestinations[destinationType];

    // Sem a URL do servidor (atributo ausente), cai no dashboard abaixo.
    if (CONCURSOS_DESTINATION_ACTION[destinationType] && concursosUrl) {
      return {
        action: CONCURSOS_DESTINATION_ACTION[destinationType],
        url: this.withOriginParam(concursosUrl),
        favoriteCourse: false
      };
    }

    // Sem a URL do playground (atributo ausente), href vazio recarregaria a
    // página em vez de navegar — o dashboard é um destino melhor que nenhum.
    if (destinationType === 'free' && this.playgroundQuestionsUrl) {
      return {
        action: FIRST_ACTION.FREE_QUESTIONS,
        url: this.withOriginParam(this.playgroundQuestionsUrl),
        favoriteCourse: false
      };
    }

    if (destinationType === 'free') {
      return {
        action: FIRST_ACTION.DASHBOARD,
        url: this.withOriginParam(`https://${this.bordDomain}/b/dashboard`),
        favoriteCourse: false
      };
    }

    if (this.answers.selectedCourse) {
      return {
        action:
          destinationType === 'directed'
            ? FIRST_ACTION.DIRECTED_QUESTIONS
            : FIRST_ACTION.COURSE,
        url: this.withOriginParam(
          `https://${this.bordDomain}/b/guia-de-estudos/${this.answers.selectedCourse}/sobre`
        ),
        favoriteCourse: true
      };
    }

    if (
      this.answers.segment === 'oab' &&
      this.answers.oabFirstAction === 'continue'
    ) {
      return {
        action: FIRST_ACTION.OAB_CONTINUE,
        url: this.withOriginParam(`https://${this.bordDomain}/b/dashboard`),
        favoriteCourse: false
      };
    }

    if (
      this.answers.segment === 'enem' &&
      this.answers.enemFirstAction === 'continue'
    ) {
      return {
        action: FIRST_ACTION.PROENEM_CONTINUE,
        url: this.withOriginParam(`https://${this.bordDomain}/b/dashboard`),
        favoriteCourse: false
      };
    }

    return {
      action: FIRST_ACTION.DASHBOARD,
      url: this.withOriginParam(`https://${this.bordDomain}/b/dashboard`),
      favoriteCourse: false
    };
  }

  // `$clickedBtn`: o botão que disparou a ação, para o estado "Salvando..." cair
  // nele. Nem sempre é um [data-destination] — "Só quero resolver questões" pode
  // ir direto para o modo livre sem passar pela sub-pergunta.
  async saveAndClose(destinationType, $clickedBtn) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    const $btn =
      $clickedBtn && $clickedBtn.length
        ? $clickedBtn
        : this.$root.find(
            '[data-action="save-and-close"]:not([data-destination])'
          );
    const originalText = $btn.text();
    $btn.prop('disabled', true).text('Salvando...');

    try {
      const segment = this.answers.segment;

      await this.persistPreferences();

      const segmentLabel = segment ? SEGMENT_PRODUCT_LABEL[segment] : undefined;
      const destination = this.resolveDestination(destinationType);

      window.UserTracker?.emit('event', {
        eventName: 'account setup completed',
        data: {
          segment: segmentLabel,
          request_path: window.location.pathname,
          setup_version: SETUP_VERSION,
          'first action chosen': destination.action,
          'mesa to elite rollout': this.mesaToEliteActive,
          education_level: this.answers.educationLevel,
          careers:
            this.answers.careers.length > 0
              ? this.answers.careers.join(',')
              : undefined,
          has_open_notice: this.answers.hasOpenNotice,
          target_job: this.answers.cargoAlvo ?? undefined,
          dedication_time: this.answers.dedicationTime ?? undefined,
          minimum_salary_cents: this.answers.salarioMinCents ?? undefined,
          already_pays_other_prep_course: this.answers.hasOtherPrepCourse,
          state: this.answers.state,
          accepts_other_states: this.answers.acceptsOtherStates,
          bar_exam_phase: this.answers.barExamPhase,
          bar_exam_subject: this.answers.barExamSubject,
          bar_exam_experience: this.answers.barExamExperience,
          enem_role: this.answers.enemRole,
          enem_subjects:
            this.answers.enemSubjects.length > 0
              ? this.answers.enemSubjects.join(',')
              : undefined,
          target_score: this.answers.targetScore,
          desired_course: this.answers.desiredCourse,
          target_university: this.answers.targetUniversity,
          military_exam: this.answers.militaryExam,
          military_experience: this.answers.militaryExperience,
          current_level: this.answers.currentLevel,
          phone: this.answers.phone,
          selected_course: this.answers.selectedCourse
        }
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      if (destination.favoriteCourse) {
        try {
          await Thor.StudyGuides.favorite(
            {},
            {
              data: { study_guide_id: this.answers.selectedCourse }
            }
          );
        } catch {
          /* ignore */
        }
      }

      // O setup V1 setava este cookie ao abrir (gatilho do onboarding guiado
      // do bord — experiment_guided_onboarding) com validade de 20 dias por
      // navegador. Expira aqui para resíduo do V1 não disparar o onboarding
      // em cima de quem concluiu o fluxo novo.
      document.cookie =
        'activationDate=; domain=.qconcursos.com; path=/; max-age=0';

      window.location.href = destination.url;
    } catch {
      this.isSaving = false;
      $btn.prop('disabled', false).text(originalText);
      toastr.error(
        'Ocorreu um erro ao salvar suas preferências. Tente novamente.'
      );
    }
  }
}

export { SetupWizard };

(() => {
  document.addEventListener('turbolinks:load', () => {
    new SetupWizard();
  });
})();
